/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import queryString from 'querystring';

import { Endpoints, Infer } from 'shared';

import { ApiClient } from '../types/api/ApiClient';
import { EntityId } from '../types/api/EntityId';
import { CreateEmailTestRequest } from '../types/superuser/CreateEmailTestRequest';
import { UpdateSystemConfigurationRequest } from '../types/superuser/UpdateSystemConfigurationRequest';
import { UpdateSystemConfigurationResponse } from '../types/superuser/UpdateSystemConfigurationResponse';
import { SUUser } from '../types/superuser/user/SUUser';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';

class SuperuserUserApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  fetchSystemConfiguration = () => {
    return this.api.get<Infer<typeof Endpoints.SystemConfigGet>['Response'][200]>('/system-config');
  };

  updateSystemConfiguration = ({ data }: { data: UpdateSystemConfigurationRequest }) => {
    return this.api.patch<UpdateSystemConfigurationResponse>('/system-config', data);
  };

  createEmailTest = ({ data }: { data: CreateEmailTestRequest }) => {
    return this.api.post('/system-config/test-email', data);
  };
  fetchUsers = () => {
    return this.api.get<SUUser[]>('/administration/user');
  };

  updateUserStatus = ({ userId, isDisabled }: { userId: EntityId; isDisabled: boolean }) => {
    return this.api.patch(
      `/administration/user/${userId}/status?${queryString.encode({
        isDisabled,
      })}`,
      {},
    );
  };

  updateSuperuserStatus = ({ userId, isSuperuser }: { userId: EntityId; isSuperuser: boolean }) => {
    return this.api.patch(
      `/administration/user/${userId}/superuser?${queryString.encode({
        isSuperuser,
      })}`,
      {},
    );
  };
}

export const SuperuserUserApiService = new SuperuserUserApiServiceClass();
